import React, { useEffect, useState, useMemo } from 'react';
import { Table, Form, Modal, Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { GetServiceCaller } from '../../../../services/serviceCaller';
import { API_URL } from '../../../../constants';
import { useDispatch } from 'react-redux';
import { updateNavbarHeading } from '../../../../constants/layout/store/filterSlice';
import moment from 'moment';
import { FaColumns } from 'react-icons/fa';
import ExportToExcel from '../../../../components/ExportToExcel';
import Pagination from '../../../../components/Pagination';

const chooserColumns = [
    { key: 'Date of Opening', label: 'Date of Opening' },
    { key: 'Req Ageing', label: 'Req Ageing' },
    { key: 'TA Transfer Date', label: 'TA Transfer Date' },
    { key: 'TA Ageing', label: 'TA Ageing' },
];

const FulfillmentReport = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(updateNavbarHeading("Fulfillment Report"));
    }, [dispatch]);

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Column Chooser state
    const [selectedColumns, setSelectedColumns] = useState([]);

    const handleColumnToggle = (columnKey) => {
        setSelectedColumns(prev =>
            prev.includes(columnKey)
                ? prev.filter(key => key !== columnKey)
                : [...prev, columnKey]
        );
    };

    // Modal state for Job Code (Requisition Stage Breakdown)
    const [showModal, setShowModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedReq, setSelectedReq] = useState({ jobCode: '', reqNo: '' });
    const [modalData, setModalData] = useState(null);

    // Modal state for Req No. (Candidate TA Leads Details)
    const [showLeadsModal, setShowLeadsModal] = useState(false);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [selectedReqNo, setSelectedReqNo] = useState('');
    const [selectedStageName, setSelectedStageName] = useState('');
    const [selectedTypeFilter, setSelectedTypeFilter] = useState('RMG');
    const [leadsData, setLeadsData] = useState({ taCount: 0, rmgCount: 0, candidates: [] });

    const fetchReport = async (isFilterClick = false) => {
        setLoading(true);
        if (isFilterClick) {
            setFilterLoading(true);
        }
        try {
            let url = `${API_URL}fulfillment-report`;
            const params = [];
            if (fromDate) params.push(`FromDate=${fromDate}`);
            if (toDate) params.push(`ToDate=${toDate}`);
            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await GetServiceCaller(url);
            if (response?.data?.status === 'success' || response?.status === 200) {
                const fetchedData = response?.data?.data || response?.data || [];
                setReportData(Array.isArray(fetchedData) ? fetchedData : []);
            } else {
                setReportData([]);
            }
        } catch (error) {
            console.error("Error fetching Fulfillment Report:", error);
            setReportData([]);
        } finally {
            setLoading(false);
            setFilterLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    // Open Job Code breakdown modal
    const handleOpenModal = async (row) => {
        const jcNo = row['JC No.'] || row['JobCode'] || '';
        const reqNo = row['Req. No.'] || row['RequisitionSubID'] || row['ReqNo'] || '';
        const reqId = row['ID'] || row['Id'] || row['id'] || jcNo || reqNo;
        setSelectedReq({ jobCode: jcNo, reqNo: reqNo, id: reqId });
        setShowModal(true);
        setModalLoading(true);
        setModalData(null);

        try {
            const lookupId = reqId || jcNo || reqNo;
            const response = await GetServiceCaller(`${API_URL}fulfillment-report/leads/${encodeURIComponent(lookupId)}`);
            if (response?.data?.status === 'success' || response?.status === 200) {
                setModalData(response?.data?.data || response?.data);
            }
        } catch (error) {
            console.error("Error fetching requisition breakdown:", error);
        } finally {
            setModalLoading(false);
        }
    };

    // Open Req No / JC No candidate leads modal
    const handleOpenLeadsModal = async (row, stageName = null, typeFilter = 'RMG') => {
        const reqNo = row['Req. No.'] || row['RequisitionSubID'] || row['ReqNo'] || row['reqNo'] || selectedReq?.reqNo || '';
        const jcNo = row['JC No.'] || row['JobCode'] || row['jobCode'] || selectedReq?.jobCode || '';
        const reqId = row['ID'] || row['Id'] || row['id'] || selectedReq?.id || jcNo || reqNo;
        setSelectedReqNo(jcNo || reqNo);
        setSelectedStageName(stageName || '');
        setSelectedTypeFilter(typeFilter || 'RMG');
        setShowLeadsModal(true);
        setLeadsLoading(true);
        setLeadsData({ taCount: 0, rmgCount: 0, candidates: [] });

        try {
            let leadsUrl = `${API_URL}requirement/${encodeURIComponent(reqId)}/leads`;
            const params = [];
            if (stageName && stageName.toLowerCase() !== 'total') {
                params.push(`stage=${encodeURIComponent(stageName)}`);
            }
            if (typeFilter) {
                params.push(`type=${encodeURIComponent(typeFilter)}`);
            }
            if (params.length > 0) {
                leadsUrl += `?${params.join('&')}`;
            }
            const response = await GetServiceCaller(leadsUrl);
            if (response?.data?.status === 'success' || response?.status === 200) {
                const payload = response?.data;
                let allList = payload?.data || [];

                if (typeFilter === 'TA') {
                    allList = payload?.taLeads || allList.filter(c => c.Type === 'TA');
                } else if (typeFilter === 'EMP') {
                    allList = payload?.rmgLeads?.filter(c => Number(c.IsEmployee) === 1) || allList.filter(c => Number(c.IsEmployee) === 1);
                } else if (typeFilter === 'SUBCON') {
                    allList = payload?.rmgLeads?.filter(c => Number(c.IsEmployee) !== 1 && Number(c.IsEmployee) !== 3) || allList.filter(c => Number(c.IsEmployee) !== 1 && Number(c.IsEmployee) !== 3);
                } else if (typeFilter === 'PIPELINE') {
                    allList = payload?.rmgLeads?.filter(c => Number(c.IsEmployee) === 3) || allList.filter(c => Number(c.IsEmployee) === 3);
                } else if (typeFilter === 'RMG') {
                    allList = payload?.rmgLeads || allList.filter(c => c.Type === 'RMG');
                } else {
                    allList = payload?.data || allList;
                }

                setLeadsData({
                    taCount: payload?.taCount ?? allList.filter(c => c.Type === 'TA').length,
                    rmgCount: payload?.rmgCount ?? allList.filter(c => c.Type === 'RMG').length,
                    candidates: allList
                });
            } else {
                setLeadsData({ taCount: 0, rmgCount: 0, candidates: [] });
            }
        } catch (error) {
            console.error("Error fetching candidate leads:", error);
            setLeadsData({ taCount: 0, rmgCount: 0, candidates: [] });
        } finally {
            setLeadsLoading(false);
        }
    };

    // Helper to render clickable numbers in main table stage cells
    const renderClickableCell = (row, val, stageName = null, typeFilter = 'RMG') => {
        const num = Number(val ?? 0);
        if (num > 0) {
            return (
                <span 
                    className="text-primary text-decoration-underline font-weight-bold" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleOpenLeadsModal(row, stageName, typeFilter)}
                >
                    {num}
                </span>
            );
        }
        return 0;
    };

    // Helper to render clickable numbers inside breakdown modal
    const renderModalClickableCell = (rowOrObj, stageName, val, typeFilter = 'RMG') => {
        const num = Number(val ?? 0);
        if (num > 0) {
            return (
                <span 
                    className="text-primary text-decoration-underline font-weight-bold" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleOpenLeadsModal(rowOrObj, stageName, typeFilter)}
                >
                    {num}
                </span>
            );
        }
        return 0;
    };

    // Helper to render type-specific row in Table 1
    const renderTypeRow = (label, dataObj, filterType) => {
        const d = dataObj || {};
        const onboarded = Number(d.onboarded || 0);
        const finalSel  = Number(d.final_select || 0);
        const r3Stage   = Number(d.client_r3_stage || 0);
        const r2Stage   = Number(d.client_r2_stage || 0);
        const r1Stage   = Number(d.client_r1_stage || 0);
        const subClient = Number(d.submitted_to_client || 0);
        const cnd       = Number(d.could_not_deploy || 0);
        const r3Rej     = Number(d.client_r3_reject || 0);
        const r2Rej     = Number(d.client_r2_reject || 0);
        const r1Rej     = Number(d.client_r1_reject || 0);
        const scrRej    = Number(d.client_screen_reject || 0);

        const rowSum = onboarded + finalSel + r3Stage + r2Stage + r1Stage + subClient + cnd + r3Rej + r2Rej + r1Rej + scrRej;

        return (
            <tr>
                <td className="font-weight-bold text-left bg-light">{label}</td>
                <td>{renderModalClickableCell(selectedReq, 'Onboarded', onboarded, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Final Select', finalSel, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R3 Stage', r3Stage, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R2 Stage', r2Stage, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R1 Stage', r1Stage, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Submitted to Client', subClient, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Could Not Deploy', cnd, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R3 Reject', r3Rej, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R2 Reject', r2Rej, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R1 Reject', r1Rej, filterType)}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client Screen Reject', scrRej, filterType)}</td>
                <td className="font-weight-bold">{renderModalClickableCell(selectedReq, 'Total', rowSum, filterType)}</td>
            </tr>
        );
    };

    // Helper to render total row in Table 1
    const renderTotalRow = (modalData) => {
        const emp = modalData?.employee || {};
        const sub = modalData?.sub_con || {};
        const pip = modalData?.pipeline || {};

        const sumStage = (key) => Number(emp[key] || 0) + Number(sub[key] || 0) + Number(pip[key] || 0);

        const onboardedTot = sumStage('onboarded');
        const finalSelTot  = sumStage('final_select');
        const r3StageTot   = sumStage('client_r3_stage');
        const r2StageTot   = sumStage('client_r2_stage');
        const r1StageTot   = sumStage('client_r1_stage');
        const subClientTot = sumStage('submitted_to_client');
        const cndTot       = sumStage('could_not_deploy');
        const r3RejTot     = sumStage('client_r3_reject');
        const r2RejTot     = sumStage('client_r2_reject');
        const r1RejTot     = sumStage('client_r1_reject');
        const scrRejTot    = sumStage('client_screen_reject');

        const grandTotal = onboardedTot + finalSelTot + r3StageTot + r2StageTot + r1StageTot + subClientTot + cndTot + r3RejTot + r2RejTot + r1RejTot + scrRejTot;

        return (
            <tr className="bg-light font-weight-bold">
                <td className="text-left font-weight-bold">Total</td>
                <td>{renderModalClickableCell(selectedReq, 'Onboarded', onboardedTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Final Select', finalSelTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R3 Stage', r3StageTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R2 Stage', r2StageTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R1 Stage', r1StageTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Submitted to Client', subClientTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Could Not Deploy', cndTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R3 Reject', r3RejTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R2 Reject', r2RejTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client R1 Reject', r1RejTot, 'RMG')}</td>
                <td>{renderModalClickableCell(selectedReq, 'Client Screen Reject', scrRejTot, 'RMG')}</td>
                <td className="font-weight-bold">{renderModalClickableCell(selectedReq, 'Total', grandTotal, 'RMG')}</td>
            </tr>
        );
    };

    // Search filter
    const filteredData = useMemo(() => {
        setCurrentPage(1);
        if (!searchTerm.trim()) return reportData;
        const term = searchTerm.toLowerCase();
        return reportData.filter(row => {
            return Object.values(row).some(val => 
                String(val || '').toLowerCase().includes(term)
            );
        });
    }, [reportData, searchTerm]);

    // Pagination data slicing
    const totalItems = filteredData.length;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = useMemo(() => {
        return filteredData.slice(startIndex, endIndex);
    }, [filteredData, startIndex, endIndex]);

    // Prepare clean data for Excel export (excluding internal DB IDs)
    const excelData = useMemo(() => {
        if (!Array.isArray(filteredData)) return [];
        return filteredData.map(item => {
            if (!item || typeof item !== 'object') return item;
            const { ID, id, ...rest } = item;
            return rest;
        });
    }, [filteredData]);

    return (
        <div className="container-fluid p-3">
            <div className="bg-white p-3 rounded-3 shadow-sm mb-3" style={{ height: 'auto' }}>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div style={{ minWidth: '220px' }}>
                            <Form.Control
                                type="text"
                                placeholder="Search JC No., Req. No..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                size="sm"
                            />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <label className="form-label mb-0 small text-muted font-weight-bold">From:</label>
                            <Form.Control
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                size="sm"
                                style={{ width: '150px' }}
                            />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <label className="form-label mb-0 small text-muted font-weight-bold">To:</label>
                            <Form.Control
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                size="sm"
                                style={{ width: '150px' }}
                            />
                        </div>
                        <button 
                            type="button"
                            className="custom-btn"
                            onClick={() => {
                                setCurrentPage(1);
                                fetchReport(true);
                            }}
                            disabled={loading}
                        >
                            {filterLoading && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>} Apply Filter
                        </button>

                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {excelData.length > 0 && (
                            <ExportToExcel data={excelData} filename="Fulfillment_Report" />
                        )}
                        <Dropdown autoClose="outside" className="d-inline-block">
                            <OverlayTrigger
                                trigger={['hover']}
                                placement="bottom"
                                overlay={<Tooltip id="tooltip-column-chooser">Column Chooser</Tooltip>}
                            >
                                <Dropdown.Toggle 
                                    as="button" 
                                    type="button" 
                                    className="btn btn-outline-secondary btn-sm p-1 px-2 d-flex align-items-center justify-content-center bg-white shadow-sm"
                                    id="column-chooser-toggle"
                                    style={{ border: '1px solid #ced4da', height: '31px', minWidth: '34px' }}
                                >
                                    <i className="bi bi-list fs-5" style={{ color: '#0f172a', lineHeight: 1 }}></i>
                                </Dropdown.Toggle>
                            </OverlayTrigger>
                            <Dropdown.Menu className="p-2 px-3 shadow border-0" align="end" style={{ minWidth: '165px' }}>
                                <Form.Group className="mb-0">
                                    {chooserColumns.map(col => (
                                        <Form.Check 
                                            key={col.key}
                                            type="checkbox"
                                            id={`col-chooser-${col.key}`}
                                            label={col.label}
                                            checked={selectedColumns.includes(col.key)}
                                            onChange={() => handleColumnToggle(col.key)}
                                            className="mb-2 small font-weight-bold"
                                        />
                                    ))}
                                </Form.Group>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </div>

            <div className="bg-white p-3 rounded-3 shadow-sm border">
                <div className="table-responsive" style={{ overflow: 'auto' }}>
                    <Table striped bordered hover size="sm" className="mb-0 text-center align-middle fulfillment-table" style={{ fontSize: '11.5px', width: '100%' }}>
                        <thead id="thcolor">
                            <tr>
                                <th rowSpan={2} style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>JC No.</th>
                                <th rowSpan={2} style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>Req. No.</th>
                                <th colSpan={12} style={{ textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Customer Stage - Overall</th>
                                {selectedColumns.map(key => {
                                    const colDef = chooserColumns.find(c => c.key === key);
                                    return (
                                        <th key={key} rowSpan={2} style={{ verticalAlign: 'middle', minWidth: '130px', whiteSpace: 'nowrap' }}>
                                            {colDef?.label || key}
                                        </th>
                                    );
                                })}
                            </tr>
                            <tr>
                                <th style={{ whiteSpace: 'nowrap' }}>Onboarded</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Final Select</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Client R3 Stage</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Client R2 Stage</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Client R1 Stage</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Submitted to Client</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Could Not Deploy</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Client R3 Reject</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Client R2 Reject</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Client R1 Reject</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Client Screen Reject</th>
                                <th style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={14 + selectedColumns.length} className="py-4 text-center">
                                        <div className="spinner-border text-primary spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedData.length > 0 ? (
                                paginatedData.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="font-weight-bold text-left" style={{ whiteSpace: 'nowrap' }}>
                                            <span 
                                                className="text-primary text-decoration-underline" 
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleOpenLeadsModal(row, null, 'RMG')}
                                            >
                                                {row['JC No.'] || row['JobCode'] || '-'}
                                            </span>
                                        </td>
                                        <td className="font-weight-bold text-left" style={{ whiteSpace: 'nowrap' }}>
                                            <span 
                                                className="text-primary text-decoration-underline" 
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleOpenModal(row)}
                                            >
                                                {row['Req. No.'] || row['RequisitionSubID'] || row['ReqNo'] || '-'}
                                            </span>
                                        </td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Onboarded'] ?? row['onboarded'], 'Onboarded', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Final Select'] ?? row['final_select'], 'Final Select', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Client R3 Stage'] ?? row['client_r3_stage'], 'Client R3 Stage', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Client R2 Stage'] ?? row['client_r2_stage'], 'Client R2 Stage', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Client R1 Stage'] ?? row['client_r1_stage'], 'Client R1 Stage', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Submitted to Client'] ?? row['submitted_to_client'], 'Submitted to Client', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Could Not Deploy'] ?? row['could_not_deploy'], 'Could Not Deploy', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Client R3 Reject'] ?? row['client_r3_reject'], 'Client R3 Reject', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Client R2 Reject'] ?? row['client_r2_reject'], 'Client R2 Reject', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Client R1 Reject'] ?? row['client_r1_reject'], 'Client R1 Reject', 'RMG')}</td>
                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Client Screen Reject'] ?? row['client_screen_reject'], 'Client Screen Reject', 'RMG')}</td>
                                        <td className="text-center font-weight-bold" style={{ whiteSpace: 'nowrap' }}>{renderClickableCell(row, row['Total'] ?? row['total'], 'Total', 'RMG')}</td>

                                        {/* Selected Column Chooser Cells */}
                                        {selectedColumns.includes('Date of Opening') && (
                                            <td className="text-center" style={{ whiteSpace: 'nowrap', minWidth: '130px' }}>{row['Date of Opening'] || '-'}</td>
                                        )}
                                        {selectedColumns.includes('Req Ageing') && (
                                            <td className="text-center" style={{ whiteSpace: 'nowrap', minWidth: '110px' }}>
                                                {row['Req Ageing'] != null ? `${row['Req Ageing']} days` : '-'}
                                            </td>
                                        )}
                                        {selectedColumns.includes('TA Transfer Date') && (
                                            <td className="text-center" style={{ whiteSpace: 'nowrap', minWidth: '130px' }}>{row['TA Transfer Date'] || '-'}</td>
                                        )}
                                        {selectedColumns.includes('TA Ageing') && (
                                            <td className="text-center" style={{ whiteSpace: 'nowrap', minWidth: '110px' }}>
                                                {row['TA Ageing'] != null ? `${row['TA Ageing']} days` : '-'}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={14 + selectedColumns.length} className="py-4 text-muted">
                                        No Fulfillment Report records found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Pagination Controls */}
                {totalItems > 0 && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 pt-3 mt-1">
                        <div className="d-flex align-items-center gap-2 small text-muted font-weight-bold">
                            <span>
                                Showing {totalItems > 0 ? startIndex + 1 : 0} - {Math.min(endIndex, totalItems)} of {totalItems}
                            </span>
                            <Form.Select
                                size="sm"
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                style={{ width: '75px', display: 'inline-block', padding: '2px 8px' }}
                            >
                                <option value={10}>10</option>
                                <option value={15}>15</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </Form.Select>
                        </div>
                        <div>
                            <Pagination
                                itemsPerPage={itemsPerPage}
                                totalItems={totalItems}
                                currentPage={currentPage}
                                onPageChange={(page) => setCurrentPage(page)}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Custom styles for sticky table headers, compact pagination & white modal close button */}
            <style>{`
                .modal-backdrop.show {
                    opacity: 0.25 !important;
                }
                .pagination {
                    margin-bottom: 0 !important;
                }
                .pagination .page-link {
                    padding: 4px 10px !important;
                    font-size: 12px !important;
                }
                .modal-header .btn-close {
                    filter: invert(1) grayscale(100%) brightness(200%);
                    opacity: 0.85;
                    background-color: transparent !important;
                    box-shadow: none !important;
                    outline: none !important;
                }
                .modal-header .btn-close:hover {
                    opacity: 1;
                    background-color: transparent !important;
                    box-shadow: none !important;
                }
                .modal-header .btn-close:focus,
                .modal-header .btn-close:active {
                    box-shadow: none !important;
                    outline: none !important;
                    background-color: transparent !important;
                    opacity: 1;
                }
                thead#thcolor th {
                    position: sticky;
                    top: 0;
                    z-index: 2;
                    background-color: var(--primary-color, #00004c) !important;
                    color: #fff !important;
                    padding: 6px 4px !important;
                    white-space: nowrap !important;
                }
                .fulfillment-table td {
                    padding: 5px 4px !important;
                    white-space: nowrap !important;
                }
            `}</style>

            {/* Job Code Breakdown Modal */}
            <Modal 
                show={showModal} 
                onHide={() => setShowModal(false)} 
                size="xl" 
                centered
            >
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: 'var(--primary-color)', color: '#fff', padding: '14px 20px' }}>
                    <Modal.Title style={{ fontSize: '16px', fontWeight: '600' }}>
                        Requisition Details ({selectedReq.reqNo || selectedReq.jobCode})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ backgroundColor: '#f8fafc' }}>
                    {modalLoading ? (
                        <div className="py-5 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading breakdown...</span>
                            </div>
                        </div>
                    ) : (
                        <div>
                            {/* TABLE 1: Breakdown by Member Type (employee, sub_con, pipeline) vs RMG Stages */}
                            <div className="table-responsive border rounded-3 shadow-sm bg-white p-2 mb-4">
                                <Table bordered hover size="sm" className="mb-0 text-center align-middle" style={{ fontSize: '13px' }}>
                                    <thead id="thcolor">
                                        <tr>
                                            <th className="text-left font-weight-bold" style={{ minWidth: '110px' }}>Type</th>
                                            <th>Onboarded</th>
                                            <th>Final Select</th>
                                            <th>Client R3 Stage</th>
                                            <th>Client R2 Stage</th>
                                            <th>Client R1 Stage</th>
                                            <th>Submitted to Client</th>
                                            <th>Could Not Deploy</th>
                                            <th>Client R3 Reject</th>
                                            <th>Client R2 Reject</th>
                                            <th>Client R1 Reject</th>
                                            <th>Client Screen Reject</th>
                                            <th className="font-weight-bold">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Employee Row */}
                                        {renderTypeRow('Employee', modalData?.employee, 'EMP')}
                                        {/* Subcon Row */}
                                        {renderTypeRow('Contractor', modalData?.sub_con, 'SUBCON')}
                                        {/* Pipeline Row */}
                                        {renderTypeRow('Pipeline', modalData?.pipeline, 'PIPELINE')}
                                        {/* Total Row */}
                                        {renderTotalRow(modalData)}
                                    </tbody>
                                </Table>
                            </div>

                            {/* TABLE 2: Internal Stage - TA FT */}
                            <div className="table-responsive border rounded-3 shadow-sm bg-white p-2" style={{ maxWidth: '520px' }}>
                                <Table bordered hover size="sm" className="mb-0 text-center align-middle" style={{ fontSize: '13px' }}>
                                    <thead id="thcolor">
                                        <tr>
                                            <th colSpan={4} className="py-2 font-weight-bold text-center">
                                                Internal Stage - TA FT
                                            </th>
                                        </tr>
                                        <tr>
                                            <th>Offer Stage</th>
                                            <th>Client/R3 Stage</th>
                                            <th>R2 Stage</th>
                                            <th>R1 Stage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{renderModalClickableCell(selectedReq, 'Offer Stage', modalData?.ta_ft?.ta_offer_stage ?? modalData?.ta_offer_stage, 'TA')}</td>
                                            <td>{renderModalClickableCell(selectedReq, 'Client/R3 Stage', modalData?.ta_ft?.ta_client_r3_stage ?? modalData?.ta_client_r3_stage, 'TA')}</td>
                                            <td>{renderModalClickableCell(selectedReq, 'R2 Stage', modalData?.ta_ft?.ta_r2_stage ?? modalData?.ta_r2_stage, 'TA')}</td>
                                            <td>{renderModalClickableCell(selectedReq, 'R1 Stage', modalData?.ta_ft?.ta_r1_stage ?? modalData?.ta_r1_stage, 'TA')}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Candidate TA Leads Details Modal */}
            <Modal show={showLeadsModal} onHide={() => setShowLeadsModal(false)} size="lg" centered>
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: 'var(--primary-color)', color: '#fff', padding: '14px 20px' }}>
                    <Modal.Title style={{ fontSize: '16px', fontWeight: '600' }}>
                        JC Details ({selectedReqNo}){selectedStageName ? ` - ${selectedStageName}` : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ backgroundColor: '#f8fafc' }}>
                    {leadsLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading Candidates...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive border rounded-3 shadow-sm bg-white" style={{ maxHeight: '380px', overflowY: 'auto' }}>
                            <Table hover bordered size="sm" className="align-middle mb-0" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '55px', position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--primary-color)', color: '#fff' }}>Sr. No</th>
                                        <th style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--primary-color)', color: '#fff' }}>Name</th>
                                        <th style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--primary-color)', color: '#fff' }}>
                                            {selectedTypeFilter === 'TA' ? 'Recruiter Name' : 'RMG POC'}
                                        </th>
                                        <th className="text-center" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--primary-color)', color: '#fff' }}>Type</th>
                                        <th className="text-center" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--primary-color)', color: '#fff' }}>Entry Date</th>
                                        <th className="text-center" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--primary-color)', color: '#fff' }}>Current Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leadsData.candidates?.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted py-4">No candidate leads found for this requisition.</td>
                                        </tr>
                                    ) : (
                                        leadsData.candidates.map((cand, idx) => {
                                            const isPipeline = Number(cand.IsEmployee) === 3 || (cand.EmploymentType || '').toLowerCase() === 'pipeline';
                                            const isEmp = !isPipeline && (cand.Type === 'TA' || Number(cand.IsEmployee) === 1 || (cand.EmploymentType || '').toLowerCase().includes('emp'));
                                            const displayType = isPipeline ? 'Pipeline' : (cand.EmploymentType || (isEmp ? 'Employee' : 'Contractor'));
                                            const rawDate = cand.AppliedDate || cand.SubmissionDate || cand.CreatedDate;
                                            const formattedDate = rawDate && moment(rawDate).isValid() ? moment(rawDate).format('DD-MM-YYYY') : '-';
                                            return (
                                                <tr key={cand.ID || idx}>
                                                    <td className="text-center">{idx + 1}</td>
                                                    <td className="fw-bold">{cand.CandidateName || "-"}</td>
                                                    <td>{cand.RecruiterName || "-"}</td>
                                                    <td className="text-center font-weight-bold">{displayType}</td>
                                                    <td className="text-center">{formattedDate}</td>
                                                    <td className="text-center font-weight-bold">{cand.LatestStatus || "-"}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-white border-top-0 pt-0 pb-3 pe-4">
                    <Button variant="secondary" onClick={() => setShowLeadsModal(false)} size="sm">
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default FulfillmentReport;
